import { faCheck, faHeart } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import Badge from 'components/base/Badge';
import Button from 'components/base/Button';
import Rating from 'components/base/Rating';
import { Product as ProductType } from 'data/e-commerce/products';
import { currencyFormat } from 'helpers/utils';
import { faHeart as farHeart } from '@fortawesome/free-regular-svg-icons';
import { Link } from 'react-router-dom';
import { addToWishlist, removeFromWishlist } from 'api/user/_requests';
import { useDispatch } from 'react-redux';
import {
  addToWishlist as addToWishlistAction,
  removeFromWishlist as removeFromWishlistAction
} from 'redux-state/user/wishlistSlice';
import toast from 'react-hot-toast';
import { useSelector } from 'react-redux';
import { RootState } from 'redux-state/store';
import { useAppContext } from 'providers/AppProvider';

const ProductCard = ({
  product,
  setProduct
}: {
  product: ProductType;
  setProduct: () => void;
}) => {
  const dispatch = useDispatch();
  const { user } = useSelector((state: RootState) => state.auth);
  const {
    config: { theme }
  } = useAppContext();

  const handleWishlistClick = async () => {
    if (!user) {
      toast.error('You must be logged in to add to wishlist');
      return;
    }
    if (user.role !== 'customer') {
      toast.error('You must be a customer to add to wishlist');
      return;
    }

    if (product.wishListed) {
      const data = {
        productId: product.id,
        variantId: product.wishlistedVariantId || null
      };
      await removeFromWishlist(data)
        .then(() => {
          dispatch(removeFromWishlistAction(data));
          setProduct();
        })
        .catch(err => {
          toast.error('Failed to remove from wishlist');
        });
    } else {
      const data = {
        productId: product.id,
        variantId: product.wishlistedVariantId || null
      };
      await addToWishlist(data)
        .then(() => {
          dispatch(addToWishlistAction(data));
          setProduct();
        })
        .catch(err => {
          toast.error('Failed to add to wishlist');
        });
    }
  };

  return (
    <div
      className={`position-relative text-decoration-none product-card h-100 ${
        theme === 'dark' ? 'bg-dark' : 'bg-white'
      } rounded-3 p-3`}
    >
      <div className="d-flex flex-column justify-content-between h-100">
        <div>
          <div className="border border-translucent rounded-3 position-relative mb-3">
            <Button
              variant={product.wishListed ? 'primary' : 'outline-primary'}
              className={classNames(
                'rounded-circle p-0 d-flex flex-center btn-wish btn-wish-primary  z-2 d-toggle-container',
                {
                  active: product.wishListed
                }
              )}
              onClick={handleWishlistClick}
            >
              {product.wishListed ? (
                <FontAwesomeIcon icon={faHeart} />
              ) : (
                <>
                  <FontAwesomeIcon icon={faHeart} className="d-block-hover" />
                  <FontAwesomeIcon icon={farHeart} className="d-none-hover" />
                </>
              )}
            </Button>
            <img
              src={product.image}
              alt={product.name}
              className="img-square w-100 rounded"
              style={{
                objectFit: 'cover',
                maxWidth: '100%'
              }}
            />
            {product.verified && (
              <Badge bg="success" className="fs-10 product-verified-badge">
                Verified
                <FontAwesomeIcon icon={faCheck} className="ms-1" />
              </Badge>
            )}
          </div>
          <Link
            to={`/product-details/${product.slug}`}
            className="stretched-link"
          >
            <h6 className="mb-2 lh-sm line-clamp-3 product-name">
              {product.name}
            </h6>
          </Link>
          {product.rating && (
            <p className="fs-9">
              <Rating readonly initialValue={product.rating} />
              {product.rated && (
                <span className="text-body-quaternary fw-semibold ms-1">
                  ({product.rated} people rated)
                </span>
              )}
            </p>
          )}
        </div>
        <div>
          {product.extra && (
            <p className={classNames(product.extraClass, 'fs-9')}>
              {product.extra}
            </p>
          )}
          {product.extra2 && (
            <p className={classNames(product.extra2Class, 'fs-9')}>
              {product.extra2}
            </p>
          )}

          {product.salePrice && (
            <>
              {product.price ? (
                <div className="d-flex align-items-center mb-1">
                  <p className="me-2 text-body text-decoration-line-through mb-0">
                    {currencyFormat(product.price)}
                  </p>
                  <h3 className="text-body-emphasis mb-0">
                    {currencyFormat(product.salePrice)}
                  </h3>
                </div>
              ) : (
                <h3 className="text-body-emphasis">
                  {currencyFormat(product.salePrice)}
                </h3>
              )}
            </>
          )}

          {product.colors && (
            <p
              className={classNames(
                'text-body-tertiary fw-semibold fs-9 lh-1',
                {
                  'mb-0': !product.dealEndTime
                }
              )}
            >
              {product.colors} colors
            </p>
          )}

          {product.dealEndTime && (
            <p className="text-success fw-bold fs-9 lh-1 mb-0">
              Deal time ends in {product.dealEndTime}
            </p>
          )}

          {product.offer && (
            <h6 className="text-success lh-1 mb-0">{product.offer} off</h6>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductCard;
